/**
 * Can the platform actually cover this payment right now?
 *
 * The queue has always *shown* the Stripe balance, but nothing compared it to
 * the row an operator was about to release, so an unaffordable payment looked
 * exactly like an affordable one until Stripe declined it. There is no
 * `expert_payment.*` code for an insufficient balance either — a decline lands
 * on the generic branch in `errors.ts`, which cannot say what went wrong. So
 * this has to be answered before the click, not after it.
 *
 * Per currency, never summed: `available` is a list because Stripe holds a
 * separate balance per currency, and adding EUR minor units to USD ones would
 * produce a number that means nothing.
 *
 * `pending` is deliberately not counted. It is money that has not settled and
 * cannot move today, so including it would make an unaffordable payment look
 * affordable — the precise failure this exists to prevent.
 */
import { centsOf } from './format';

export interface Amount {
  cents: string;
  currency: string;
}

export interface Shortfall {
  /** What is actually available in the payment's own currency. */
  available: Amount;
  /** How far short, in minor units of that currency. Always > 0. */
  shortfallCents: number;
}

/**
 * `null` means "no warning": either affordable, or not yet knowable.
 *
 * An absent balance read — still loading, or the api could not reach Stripe —
 * returns null rather than a warning. Claiming a shortfall on missing data would
 * block a legitimate payout on a display failure, and the api already surfaces
 * the read failure separately in the preflight panel.
 *
 * A currency Stripe holds no bucket for IS a shortfall, not unknown: no bucket
 * means nothing available to draw on.
 */
export function shortfallFor(amount: Amount, available: Amount[] | undefined): Shortfall | null {
  if (!available) return null;

  const bucket = available.find((a) => a.currency === amount.currency) ?? {
    cents: '0',
    currency: amount.currency,
  };

  const needed = centsOf(amount);
  const have = centsOf(bucket);
  if (!Number.isFinite(needed) || !Number.isFinite(have)) return null;
  if (have >= needed) return null;

  return { available: bucket, shortfallCents: needed - have };
}
